import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Box, Typography, Button, TextField, Alert, CircularProgress, Checkbox, FormControlLabel, Paper,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SignaturePad from '../components/SignaturePad';
import SchoolContractDocument from '../components/SchoolContractDocument';

// /contract/:token — where a school official reviews and signs the court rental
// agreement from their emailed link. No account: the token is the access, and
// every read and write goes through a callable that checks it.

const gold = '#C9A84C';
const dark = '#0D1E35';
const fmt = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

export default function SignContract() {
  const { token } = useParams();

  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typedName, setTypedName] = useState('');
  const [signature, setSignature] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await httpsCallable(getFunctions(), 'getSchoolContractForSigning')({ token });
      setView(res.data);
      setError('');
    } catch (e) {
      setError(e.message || 'This link could not be opened.');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleSign = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await httpsCallable(getFunctions(), 'signSchoolContract')({ token, typedName, signature, agree });
      setView(res.data);
    } catch (e) {
      setError(e.message || 'Signing failed. Please try again.');
    }
    setBusy(false);
  };

  const me = view ? view.signers[view.you] : null;
  const signed = !!me?.signedAt;
  const paid = view?.payment?.status === 'paid';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: dark, py: { xs: 2, sm: 4 }, px: 2 }}>
      <Box sx={{ maxWidth: 860, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <img src="/torch_logo.png" alt="" style={{ height: 44 }} />
          <Box>
            <Typography sx={{ color: gold, fontWeight: 700, fontSize: '1.15rem' }}>Court Rental Agreement</Typography>
            {view && <Typography variant="body2" sx={{ color: '#cfd8e3' }}>{view.schoolName}</Typography>}
          </Box>
          {view && (
            <Button startIcon={<PrintIcon />} onClick={() => window.print()} sx={{ ml: 'auto', color: gold }}>
              Print / PDF
            </Button>
          )}
        </Box>

        {loading && <Box sx={{ textAlign: 'center', mt: 8 }}><CircularProgress sx={{ color: gold }} /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {view && (
          <>
            {signed && (
              <Alert icon={<CheckCircleOutlineIcon />} severity="success" sx={{ mb: 2 }}>
                You signed this agreement.
                {view.status === 'signed' ? ' Every party has signed.' : ' We’ll email you a copy once every party has signed.'}
                {paid ? ' Payment received — thank you.' : ''}
              </Alert>
            )}

            {signed && !paid && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>Prepayment: {fmt(view.totalCents)}</Typography>
                <Typography variant="body2" sx={{ color: '#555' }}>
                  Payable by check before the season starts — the agreement says where to mail it.
                </Typography>
              </Paper>
            )}

            <SchoolContractDocument html={view.html} signers={view.signers} director={view.director}
              docHash={view.docHash} schoolName={view.schoolName} />

            {!signed && view.status !== 'void' && (
              <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Sign as {me?.name}</Typography>
                {me?.title && <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>{me.title}, {view.schoolName}</Typography>}
                <TextField fullWidth label="Type your full name" value={typedName}
                  onChange={e => setTypedName(e.target.value)} sx={{ mb: 2 }} />
                <SignaturePad onChange={setSignature} disabled={busy} />
                <FormControlLabel sx={{ mt: 1 }}
                  control={<Checkbox checked={agree} onChange={e => setAgree(e.target.checked)} />}
                  label="I have read this agreement, I am authorized to sign it, and I agree to its terms. I agree my electronic signature is as binding as a handwritten one." />
                <Box sx={{ mt: 2 }}>
                  <Button variant="contained" size="large" onClick={handleSign}
                    disabled={busy || !agree || !signature || typedName.trim().length < 2}
                    sx={{ backgroundColor: dark, color: gold, '&:hover': { backgroundColor: '#1E3A5F' } }}>
                    {busy ? <CircularProgress size={22} sx={{ color: gold }} /> : 'Sign agreement'}
                  </Button>
                </Box>
              </Paper>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
